import { SurveyFullViewModel } from "../TripRateNew/ViewModels/SurveyFullViewModel";
import PrimaryFiltersViewModel from "../TripRate/ViewModels/PrimaryFiltersViewModel";
import SecondaryFiltersViewModel from "../TripRate/ViewModels/SecondaryFilteringViewModel";
import { FetchAction } from "../TripRate/TripRateContext";
import { useContext } from "react";
import { SessionContext } from "../Contexts/SessionContext";
import { SessionState } from "../Contexts/SessionState";

export const isPublicTransportNotKnown = (reason?: string | null) => {
    const normalized = reason?.trim().toUpperCase();
    return !normalized || normalized === "NK";
};

/* ---------------------------------------------
   Core filter entry point
--------------------------------------------- */
export function getFilteredSurveys(
    surveys: SurveyFullViewModel[],
    opts?: {
        fetchAction?: FetchAction;
        ignoreHeaderIndex?: boolean;
    }
) {
    const { sessionState } = useContext(SessionContext)
    return getFilteredSurveysForSession(surveys, sessionState, opts);
}

export function getFilteredSurveysForSession(
    surveys: SurveyFullViewModel[],
    sessionState: SessionState,
    opts?: {
        fetchAction?: FetchAction;
        ignoreHeaderIndex?: boolean;
    }
) {
    const { fetchAction, ignoreHeaderIndex } = opts ?? {};
    let list = surveys;
    var landVm = sessionState.landusePage;

    if (landVm && landVm.sites && landVm.sites.length > 0) {
        const selectedSiteRefs = new Set(landVm.sites);

        if (!landVm.isAllSelected) {
            // same as: landVM.IsAllSelected == false && Sites.Contains(w.Reference)
            list = list.filter(s => selectedSiteRefs.has(s.reference));
        } else {
            // same as: landVM.IsAllSelected == true && !Sites.Contains(w.Reference)
            list = list.filter(s => !selectedSiteRefs.has(s.reference));
        }
    }

    if (!ignoreHeaderIndex) {
        list = filterByHeaderIndex(list, sessionState.primaryFilters?.headerIndex);
    }
    list = applyPrimary(list, sessionState.primaryFilters, fetchAction, ignoreHeaderIndex);
    list = applySecondary(list, sessionState.secondaryFilters, sessionState.tripRateMain, fetchAction);

    if (sessionState?.residentialUnitTypes && sessionState.residentialUnitTypes.length < 7) {
        const keyMap = [
            "hasDetachedHouses",
            "hasSemiDetachedHouses",
            "hasTerracedHouses",
            "hasBungalows",
            "hasFlatsInHouses",
            "hasFlatsInBlocks",
            "hasTownHouses",
        ];

        // An unchecked type excludes a survey if that survey contains the type.
        const excludedProps = keyMap.filter((_, id) => !sessionState.residentialUnitTypes.includes(id));

        list = list.filter((s) => {
            // Keep the survey only when it contains none of the excluded types.
            return excludedProps.every((prop) => s?.[prop] !== true);
        });
    }

    return list;
}

export function getFilteredSurveyRefs(
    surveys: SurveyFullViewModel[]
) {
    const filteredSurveys = getFilteredSurveys(surveys);
    return filteredSurveys.map(s => s.surveyRef);
}

/* ---------------------------------------------
   Filters
--------------------------------------------- */
function filterByHeaderIndex(
    src: SurveyFullViewModel[],
    headerIndex?: number | string | null,
): SurveyFullViewModel[] {
    headerIndex = typeof headerIndex === "string" ? parseInt(headerIndex, 10) : headerIndex;
    if (headerIndex == null || headerIndex < 0 || headerIndex > 3) return src;
    const param = `param${headerIndex + 1}` as keyof SurveyFullViewModel;
    return src.filter(s => s[param] != null);
}

function applyPrimary(
    src: SurveyFullViewModel[],
    vm: PrimaryFiltersViewModel | null | undefined,
    fetchAction?: FetchAction,
    ignoreHeaderIndex?: boolean
) {
    if (!vm) return src;
    let list = src;

    if (vm.daysOfWeek && vm.daysOfWeek.length !== 7 && fetchAction !== "GetDaysOfWeekRanges") {
        list = list.filter(s => vm.daysOfWeek!.includes(s.dayOfWeekIndex ?? -1));
    }

    if (vm.serviceVehicles && vm.serviceVehicles.length !== 2 && fetchAction !== "GetServiceVehicles") {
        list = list.filter(s => vm.serviceVehicles!.includes(s.servicingVehicles));
    }

    if (!ignoreHeaderIndex && vm.minHeader != null && vm.maxHeader != null) {
        const headerIndex = vm.headerIndex ?? 0;
        const min = toNum(vm.minHeader);
        const max = toNum(vm.maxHeader);
        if (min != null && max != null) {
            list = list.filter(s => {
                const headerParams = [s.param1, s.param2, s.param3, s.param4];
                const headerValue = headerParams[headerIndex];
                if (headerValue == null) return false;
                return Number(headerValue) >= min && Number(headerValue) <= max;
            });
        }
    }

    if (vm.surveyTypes && vm.surveyTypes.length !== 2 && fetchAction !== "SurveysToInclude") {
        list = list.filter(s => {
            const t = (s as any).surveyType as number | undefined;
            return t != null && vm.surveyTypes!.includes(t);
        });
    }

    if (vm.locationTypes && vm.locationTypes.length !== 7 && fetchAction !== "GetMainLocationTypes") {
        list = list.filter(s => s.locationId != null && vm.locationTypes!.includes(s.locationId!));
    }

    if (vm.subLocationTypes && vm.subLocationTypes.length !== 10 && fetchAction !== "GetSubLocationTypes") {
        list = list.filter(s => s.subLocationId != null && vm.subLocationTypes!.includes(s.subLocationId!));
    }

    if (!vm.parkingSpacesChecked && vm.minParkingSpaces != null && vm.maxParkingSpaces != null) {
        list = list.filter(s => numBetween(s.parkingTotal, vm.minParkingSpaces ?? 0, vm.maxParkingSpaces ?? -1));
    }

    const min = vm.minParkingSpacesPerDwelling;
    const max = vm.maxParkingSpacesPerDwelling;
    if (!vm.parkingSpacesPerDwellingChecked && min != null && max != null) {
        list = list.filter(s => {
            const total = toNum(s.parkingTotal);
            const denom = toNum(s.param2);
            if (total == null || denom == null || denom === 0) return false;
            const ratio = total / denom;
            return numBetween(ratio, min, max);
        });
    }

    if (!vm.bedroomPerDwellingChecked && vm.minBedroomPerDwelling != null && vm.maxBedroomPerDwelling != null) {
        list = list.filter(s => numBetween(s.bedroomPerDwelling, vm.minBedroomPerDwelling ?? 0, vm.maxBedroomPerDwelling ?? -1));
    }

    if (!vm.percentDwellingsChecked) {
        if (vm.minPercentDwellings != null && vm.percentDwellingsOption === "min") {
            list = list.filter(s => (s.privatePercentage ?? -Infinity) >= vm.minPercentDwellings!);
        }
        if (vm.maxPercentDwellings != null && vm.percentDwellingsOption === "max") {
            list = list.filter(s => (s.privatePercentage ?? Infinity) <= vm.maxPercentDwellings!);
        }
    }

    if (vm.publicTransportProvision && vm.publicTransportMin != null && vm.publicTransportMax != null) {
        const min = vm.publicTransportMin;
        const max = vm.publicTransportMax;
        const incNull = vm.includeNullRowsForPublicTransport;
        const isUnknown = (x: SurveyFullViewModel) =>
            x.ptpweekday0719 == null &&
            x.ptpweekday0710 == null &&
            x.ptpweekday1619 == null &&
            x.ptpsaturday0719 == null &&
            x.ptpsunday0719 == null &&
            (x.dataVersion !== "26.8.9" || isPublicTransportNotKnown(x.publicTransportReasonForBlank));
        const inRange = (v?: number) => v != null && v >= min && v <= max;

        switch (vm.publicTransportProvision) {
            case 1: list = list.filter(s => inRange(s.ptpweekday0719) || (incNull && isUnknown(s))); break;
            case 2: list = list.filter(s => inRange(s.ptpweekday0710) || (incNull && isUnknown(s))); break;
            case 3: list = list.filter(s => inRange(s.ptpweekday1619) || (incNull && isUnknown(s))); break;
            case 4: list = list.filter(s => inRange(s.ptpsaturday0719) || (incNull && isUnknown(s))); break;
            case 5: list = list.filter(s => inRange(s.ptpsunday0719) || (incNull && isUnknown(s))); break;
        }
    }

    return list;
}

function applySecondary(
    src: SurveyFullViewModel[],
    vm: SecondaryFiltersViewModel | null | undefined,
    landuse: string,
    fetchAction?: FetchAction,
) {
    if (!vm) return src;
    let list = src;

    if (!vm.Population500MeterChecked) {
        list = list.filter(s => numBetween(s.pop500, vm.population500MeterMin, vm.population500MeterMax));
    }

    if (!vm.siteOperationsChecked && vm.siteOperationsChecked != undefined) {
        const percentage = vm.siteOperationPercentage;
        const isMin = vm.siteOperationIsMin;



        list = list.filter(s => {
            const val =
                vm.siteOperation === 0 ? s.siteOffices :
                    vm.siteOperation === 1 ? s.siteResearchAndDev :
                        vm.siteOperation === 2 ? s.siteLightIndustrial :
                            vm.siteOperation === 3 ? s.siteGeneralIndustrial :
                                vm.siteOperation === 4 ? s.siteStorageAndDistribution :
                                    undefined;
            if (val == null) return false;
            return isMin ? val >= percentage : val <= percentage;
        });
    }

    if (vm.useClasses && vm.useClasses.length !== 28 && fetchAction !== "GetSecondaryUseClass") {
        list = list.filter(s => s.useClass != null && vm.useClasses!.includes(s.useClass!));
    }

    if (vm.populationOneMile && vm.populationOneMile.length !== 10 && fetchAction !== "GetPopulationOneMile") {
        list = list.filter(s => s.pop1MileId != null && vm.populationOneMile!.includes(s.pop1MileId!));
    }

    if (vm.populationFiveMile && vm.populationFiveMile.length !== 10 && fetchAction !== "GetPopulationFiveMile") {
        list = list.filter(s => s.pop5MileId != null && vm.populationFiveMile!.includes(s.pop5MileId!));
    }

    if (vm.carOwnership && vm.carOwnership.length !== 10 && fetchAction !== "GetCarOwnershipFiveMile") {
        list = list.filter(s => vm.carOwnership!.includes(s.car5Id ?? 0));
    }

    //if (vm.ptalRating && vm.ptalRating.length !== 10 && fetchAction !== "GetPtalRating") {
    //    list = list.filter(s => s.ptal != null && vm.ptalRating!.includes((s as any).ptal));
    //}

    if (vm.ptalRating && vm.ptalRating.length !== 10 && fetchAction !== "GetPtalRating") { // fixed ptal filtering
        const ratings = vm.ptalRating.map(r => r?.toLowerCase());

        list = list.filter(
            s => s.ptal != null && ratings.includes((s as any).ptal.toLowerCase()));

    }


    if (vm.travelPlan && vm.travelPlan.length !== 3 && fetchAction !== "GetTravelPlan") {
        list = list.filter(s => s.travelPlan != null && vm.travelPlan!.includes(s.travelPlan!));
    }

    if (vm.petrolFillingStation && fetchAction !== "GetPetrolFillingStation" && landuse === "01") {
        list = list.filter(
            s => s.petrolFillingStation != null && vm.petrolFillingStation!.includes(String(s.petrolFillingStation))
        );
    }

    return list;
}

/* ---------------------------------------------
   Helpers
--------------------------------------------- */
const toNum = (v: unknown): number | null => {
    if (v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const numBetween = (v: number | undefined, min: number, max: number) => {
    if (v == null) return false;
    return v >= min && v <= max;
};
